import React, { useEffect, useState } from 'react';
import { collabApi } from '../utils/collab';
import '../styles/Preferences.css';

/**
 * **Preferences** — the user's own settings, reached from the user menu.
 *
 * Deliberately a SCREEN rather than a block inside the dropdown. The first preference
 * (chat visibility) already needed three options with a sentence of explanation each,
 * which turned the menu into something you scroll; the second one would have made it
 * unusable. A menu is for going somewhere, not for reading.
 *
 * ── Adding a preference ──────────────────────────────────────────────────────────────
 * Everything a preference needs is in `users.prefs` (a JSON blob, merged on write) and
 * `POST /api/users/me/prefs`. To add one:
 *
 *   1. accept its key in `collab_api.users_me_prefs()` — **unknown keys are dropped**,
 *      deliberately, so the column cannot be used as storage by a crafted request;
 *   2. add a section here, following the shape of `ChatVisibility` below;
 *   3. read it wherever it applies, TOTALLY — a missing key is its default, a bad value
 *      is its default, and neither is an error (Critical Note #68).
 *
 * The page is NOT an admin screen. Tenant-wide settings live at Admin → Settings, and the
 * two must not drift into each other: this one is "how I want the app to behave for me".
 */

const VISIBILITY_OPTIONS = [
  {
    value: 'visible',
    name: 'Visible',
    hint: 'Colleagues see that you are online, and can start a chat with you.',
  },
  {
    value: 'invisible',
    name: 'Appear offline',
    hint: 'You drop off everyone’s rail, but you still receive messages and can still '
        + 'start one yourself. You keep seeing who is online.',
  },
  {
    value: 'off',
    name: 'Hide me from chat',
    hint: 'Off entirely. Anyone who tries to write to you is told you have chat turned off '
        + '— nothing is silently dropped.',
  },
];

function Section({ title, blurb, children }) {
  return (
    <section className="prefs-section">
      <h2>{title}</h2>
      {blurb && <p className="prefs-blurb">{blurb}</p>}
      {children}
    </section>
  );
}

export default function Preferences() {
  const [loading, setLoading] = useState(true);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [me, setMe] = useState(null);
  const [visibility, setVisibility] = useState('visible');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      collabApi.me().catch(() => null),
      collabApi.settings().catch(() => null),
    ]).then(([mine, settings]) => {
      if (!alive) return;
      if (mine?.user) {
        setMe(mine.user);
        const v = mine.user.prefs?.chat_visibility;
        setVisibility(VISIBILITY_OPTIONS.some((o) => o.value === v) ? v : 'visible');
      }
      // Absent or unreadable settings read as ENABLED — the tenant gate fails open here
      // (Critical Note #70's neighbour: only an explicit `enabled = 0` turns chat off).
      setChatEnabled(settings ? settings.enabled !== false : true);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const choose = async (value) => {
    const previous = visibility;
    setVisibility(value);      // optimistic — this control has to feel instant
    setError('');
    setSaved('');
    try {
      await collabApi.savePrefs({ chat_visibility: value });
      setSaved('Saved.');
    } catch (err) {
      setVisibility(previous); // ...but never lie about what was saved
      setError(err.message || 'Could not save that preference.');
    }
  };

  if (loading) return <div className="prefs-page"><div className="prefs-loading">Loading preferences…</div></div>;

  return (
    <div className="prefs-page">
      <div className="prefs-head">
        <div className="prefs-crumb">Your account</div>
        <h1>Preferences</h1>
        <p className="prefs-sub">
          {me?.display_name || 'You'}
          {me?.email ? ` · ${me.email}` : ''}
        </p>
      </div>

      {chatEnabled ? (
        <Section
          title="Chat visibility"
          blurb="Whether colleagues can see that you are in the app, and whether they can write
                 to you. You can change this at any time."
        >
          <div className="prefs-options">
            {VISIBILITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`collab-pref-option${visibility === opt.value ? ' collab-pref-option--on' : ''}`}
                onClick={() => choose(opt.value)}
                aria-pressed={visibility === opt.value}
              >
                <span className="collab-pref-radio" aria-hidden="true" />
                <span className="collab-pref-text">
                  <span className="collab-pref-name">{opt.name}</span>
                  <span className="collab-pref-hint">{opt.hint}</span>
                </span>
              </button>
            ))}
          </div>

          {error && <div className="collab-refusal" role="alert">{error}</div>}
          {saved && <div className="prefs-saved" role="status">{saved}</div>}

          <p className="prefs-note">
            <strong>Appear offline</strong> still lets people reach you — it only takes you off
            the rail. <strong>Hide me from chat</strong> takes you out completely, and anyone
            who tries to write to you is told so rather than having their message disappear.
          </p>
        </Section>
      ) : (
        <Section title="Chat visibility">
          <p className="prefs-empty">
            Collaboration is switched off for this account, so there is nothing to set here.
            An Orcanos administrator can turn it on under Admin → Settings.
          </p>
        </Section>
      )}
    </div>
  );
}
